'use client';

import {
  type KeyboardEvent,
  type MouseEvent,
  type UIEvent,
  type UIEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type {
  CarouselHookPropsInterface,
  CarouselHookReturnInterface,
} from '../types/use-carousel.types';

const NOOP = (): void => {};

export const useCarousel = (props: CarouselHookPropsInterface): CarouselHookReturnInterface => {
  const {
    scrollLeft = 0,
    scrollToSlide,
    onNextClick = NOOP,
    onPreviousClick = NOOP,
    onScroll = NOOP,
    onVisibleSlidesChange = NOOP,
  } = props;

  const [previousEnabled, setPreviousEnabled] = useState<boolean>(false);
  const [nextEnabled, setNextEnabled] = useState<boolean>(false);

  const firstSlide = useRef<HTMLLIElement>(null);
  const scrollContainer = useRef<HTMLUListElement>(null);
  const sectionStart = useRef<HTMLAnchorElement>(null);
  const sectionEnd = useRef<HTMLAnchorElement>(null);
  const visibilityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const delayedVisibilityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navControlAnimationFrameRef = useRef<number | null>(null);
  const lastVisibleSlidesRef = useRef<Array<number>>([]);

  const getSlideWidth = useCallback((): number => {
    if (firstSlide.current) {
      return firstSlide.current.getBoundingClientRect().width;
    }

    return 0;
  }, []);

  // Utility function to check if two arrays are equal
  const arraysEqual = useCallback((a: Array<number>, b: Array<number>): boolean => {
    return a.length === b.length && a.every((val, i) => val === b[i]);
  }, []);

  // Get all slides that are fully visible within the container
  const getFullyVisibleSlides = useCallback((): Array<number> => {
    if (!scrollContainer.current) {
      return [];
    }

    const container = scrollContainer.current;
    const containerRect = container.getBoundingClientRect();
    const slides = container.querySelectorAll('[data-c11n-component="Carousel.Slide"]');

    const fullyVisible: Array<number> = [];

    slides.forEach((slide, index) => {
      const slideRect = slide.getBoundingClientRect();

      // Check if slide is fully within container bounds (with small tolerance for rounding)
      const isFullyVisible =
        slideRect.left >= containerRect.left - 1 && slideRect.right <= containerRect.right + 1;

      if (isFullyVisible) {
        fullyVisible.push(index);
      }
    });

    return fullyVisible;
  }, []);

  // Debounced function to check visibility and trigger callback if changed
  const triggerVisibilityCheck = useCallback(() => {
    if (visibilityTimeoutRef.current) {
      clearTimeout(visibilityTimeoutRef.current);
    }

    visibilityTimeoutRef.current = setTimeout(() => {
      const currentVisible = getFullyVisibleSlides();

      // Only fire if the visible slides actually changed
      if (!arraysEqual(currentVisible, lastVisibleSlidesRef.current)) {
        lastVisibleSlidesRef.current = currentVisible;
        onVisibleSlidesChange(currentVisible);
      }

      visibilityTimeoutRef.current = null;
    }, 150);
  }, [getFullyVisibleSlides, onVisibleSlidesChange, arraysEqual]);

  const scheduleVisibilityCheck = useCallback(
    (delay = 300) => {
      if (delayedVisibilityTimeoutRef.current) {
        clearTimeout(delayedVisibilityTimeoutRef.current);
      }

      delayedVisibilityTimeoutRef.current = setTimeout(() => {
        delayedVisibilityTimeoutRef.current = null;
        triggerVisibilityCheck();
      }, delay);
    },
    [triggerVisibilityCheck],
  );

  const scrollBy = useCallback((pixels: number): void => {
    if (scrollContainer.current) {
      scrollContainer.current.scrollLeft += pixels;
    }
  }, []);

  const scrollToSlideByIndex = useCallback(
    (slideIndex: number): void => {
      if (!scrollContainer.current) {
        return;
      }

      const slides = scrollContainer.current.querySelectorAll(
        '[data-c11n-component="Carousel.Slide"]',
      );

      const targetSlide = slides[slideIndex] as HTMLElement;
      if (!targetSlide) {
        return;
      }

      targetSlide.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      });

      scheduleVisibilityCheck();
    },
    [scheduleVisibilityCheck],
  );

  const updateNavControlStates = useCallback((): void => {
    if (scrollContainer.current) {
      setPreviousEnabled(scrollContainer.current.scrollLeft !== 0);
      // Add 1px to account for potential rounding issues
      setNextEnabled(
        scrollContainer.current.scrollWidth >
          scrollContainer.current.clientWidth + scrollContainer.current.scrollLeft + 1,
      );
    }
  }, []);

  const scheduleNavControlStateUpdate = useCallback((): void => {
    if (navControlAnimationFrameRef.current !== null) {
      return;
    }

    navControlAnimationFrameRef.current = window.requestAnimationFrame(() => {
      navControlAnimationFrameRef.current = null;
      updateNavControlStates();
    });
  }, [updateNavControlStates]);

  const handleOnScroll: UIEventHandler<HTMLUListElement> = useCallback(
    (e: UIEvent<HTMLUListElement>): void => {
      scheduleNavControlStateUpdate();
      onScroll(e);
      triggerVisibilityCheck();
    },
    [onScroll, scheduleNavControlStateUpdate, triggerVisibilityCheck],
  );

  const nextPage = useCallback(
    (e: MouseEvent<Element>): void => {
      if (scrollContainer.current) {
        const rect = scrollContainer.current.getBoundingClientRect();
        const halfSlideWidth = getSlideWidth() * 0.5;

        scrollBy(rect.width - halfSlideWidth);
        onNextClick(e);

        scheduleVisibilityCheck();
      }
    },
    [getSlideWidth, onNextClick, scrollBy, scheduleVisibilityCheck],
  );

  const previousPage = useCallback(
    (e: MouseEvent<Element>): void => {
      if (scrollContainer.current) {
        const rect = scrollContainer.current.getBoundingClientRect();
        const halfSlideWidth = getSlideWidth() * 0.5;
        scrollBy(-rect.width + halfSlideWidth);
        onPreviousClick(e);

        scheduleVisibilityCheck();
      }
    },
    [getSlideWidth, onPreviousClick, scrollBy, scheduleVisibilityCheck],
  );

  const scrollToFocusedEl = useCallback(
    (e: KeyboardEvent): void => {
      if (e.code === 'Tab') {
        const focusElement = document.activeElement as HTMLElement;
        const scrollEl = scrollContainer.current;

        const focusPosition = focusElement.getBoundingClientRect();
        const parentPosition = scrollEl?.getBoundingClientRect();

        if (focusPosition && parentPosition) {
          if (focusPosition.right > parentPosition.right) {
            const overscrollAmount = focusPosition.right - parentPosition.right;
            const slideWidth = getSlideWidth();
            const scrollAmount =
              overscrollAmount <= slideWidth ? slideWidth : overscrollAmount + slideWidth;

            scrollBy(scrollAmount);
          }

          if (focusPosition.left < parentPosition.left) {
            const overscrollAmount = parentPosition.left - focusPosition.left;
            const slideWidth = getSlideWidth();
            const scrollAmount =
              overscrollAmount <= slideWidth ? slideWidth : overscrollAmount + slideWidth;

            scrollBy(-scrollAmount);
          }
        }
      }
    },
    [getSlideWidth, scrollBy],
  );

  // Handle skip link interactions
  const handleSkipToEnd = useCallback((e: MouseEvent): void => {
    e.preventDefault();
    sectionEnd.current?.focus();
  }, []);

  const handleSkipToStart = useCallback((e: MouseEvent): void => {
    e.preventDefault();
    sectionStart.current?.focus();
  }, []);

  // Initial setup and cleanup
  useEffect(() => {
    let resizeObserver: ResizeObserver | undefined;

    if (typeof ResizeObserver !== 'undefined' && scrollContainer.current) {
      resizeObserver = new ResizeObserver(() => {
        scheduleNavControlStateUpdate();
        triggerVisibilityCheck();
      });
      resizeObserver.observe(scrollContainer.current);
    } else {
      window.addEventListener('resize', scheduleNavControlStateUpdate);
    }

    updateNavControlStates();
    triggerVisibilityCheck();

    return () => {
      if (navControlAnimationFrameRef.current !== null) {
        window.cancelAnimationFrame(navControlAnimationFrameRef.current);
        navControlAnimationFrameRef.current = null;
      }

      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', scheduleNavControlStateUpdate);
      }
    };
  }, [scheduleNavControlStateUpdate, triggerVisibilityCheck, updateNavControlStates]);

  useEffect(() => {
    return () => {
      if (navControlAnimationFrameRef.current !== null) {
        window.cancelAnimationFrame(navControlAnimationFrameRef.current);
        navControlAnimationFrameRef.current = null;
      }

      if (visibilityTimeoutRef.current) {
        clearTimeout(visibilityTimeoutRef.current);
        visibilityTimeoutRef.current = null;
      }

      if (delayedVisibilityTimeoutRef.current) {
        clearTimeout(delayedVisibilityTimeoutRef.current);
        delayedVisibilityTimeoutRef.current = null;
      }
    };
  }, []);

  // Update scroll position when scrollLeft prop changes
  // Only run when scrollToSlide is not being used (they should never be used together)
  useEffect(() => {
    if (scrollContainer.current && scrollToSlide === undefined) {
      scrollContainer.current.scrollLeft = scrollLeft;
      scheduleNavControlStateUpdate();
      triggerVisibilityCheck();
    }
  }, [scheduleNavControlStateUpdate, scrollLeft, scrollToSlide, triggerVisibilityCheck]);

  // Handle scrollToSlide prop changes
  useEffect(() => {
    if (scrollToSlide !== undefined) {
      scrollToSlideByIndex(scrollToSlide);
    }
  }, [scrollToSlide, scrollToSlideByIndex]);

  return useMemo(
    () => ({
      previousEnabled,
      nextEnabled,
      firstSlide,
      scrollContainer,
      sectionStart,
      sectionEnd,
      handleOnScroll,
      scrollToFocusedEl,
      handleSkipToStart,
      handleSkipToEnd,
      nextPage,
      previousPage,
      updateNavControlStates,
    }),
    [
      previousEnabled,
      nextEnabled,
      handleOnScroll,
      scrollToFocusedEl,
      handleSkipToStart,
      handleSkipToEnd,
      nextPage,
      previousPage,
      updateNavControlStates,
    ],
  );
};
